import { useState } from 'react';
import { Bell, X } from 'lucide-react';
import { useBrowserNotifications } from '../hooks/useBrowserNotifications.js';
import { subscribeToPushNotifications } from '../lib/pushNotifications.js';

const PROMPT_DISMISSED_KEY = 'browserNotificationPromptDismissed';

/**
 * Banner para solicitar permiso de notificaciones del navegador
 */
export function BrowserNotificationPrompt() {
  const { isSupported, isDefault, requestPermission } = useBrowserNotifications();
  const [isDismissed, setIsDismissed] = useState(() => {
    return localStorage.getItem(PROMPT_DISMISSED_KEY) === 'true';
  });
  const [isRequesting, setIsRequesting] = useState(false);

  // No mostrar si no está soportado, ya fue respondido, o fue descartado
  if (!isSupported || !isDefault || isDismissed) {
    return null;
  }

  const handleEnable = async () => {
    setIsRequesting(true);
    const permission = await requestPermission();
    setIsRequesting(false);
    
    if (permission === 'granted') {
      // Ocultar el banner
      setIsDismissed(true);
      localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
      
      // 🔔 IMPORTANTE: Suscribir a Push Notifications inmediatamente
      // Esto debe hacerse aquí porque Chrome requiere que la suscripción
      // venga de una interacción directa del usuario (el click en "Activar")
      try {
        await subscribeToPushNotifications();
        console.log('✅ Suscrito a push notifications desde el prompt');
      } catch (error) {
        console.error('Error suscribiendo a push desde prompt:', error);
      }
    }
  };

  const handleDismiss = () => {
    setIsDismissed(true);
    localStorage.setItem(PROMPT_DISMISSED_KEY, 'true');
  };

  return (
    <div className="fixed bottom-4 right-4 z-50 max-w-md animate-slideInRight">
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl shadow-2xl border border-white/10 p-6">
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 p-1 rounded-lg hover:bg-white/10 transition-colors"
          aria-label="Cerrar"
        >
          <X className="h-4 w-4 text-white" />
        </button>

        <div className="flex items-start gap-4">
          <div className="p-3 bg-white/10 rounded-xl">
            <Bell className="h-6 w-6 text-white" />
          </div>
          
          <div className="flex-1">
            <h3 className="text-lg font-bold text-white mb-2">
              Activa las Notificaciones
            </h3>
            <p className="text-sm text-white/80 mb-4">
              Recibe alertas instantáneas de nuevos pedidos, incluso cuando estés en otra pestaña o aplicación.
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={handleEnable}
                disabled={isRequesting}
                className="flex-1 px-4 py-2 bg-white text-indigo-600 rounded-lg font-semibold text-sm hover:bg-white/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isRequesting ? 'Activando...' : 'Activar'}
              </button>
              <button
                onClick={handleDismiss}
                className="px-4 py-2 bg-white/10 text-white rounded-lg font-semibold text-sm hover:bg-white/20 transition-colors"
              >
                Ahora no
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
